import { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';

import { cn } from '@/lib/cn';
import {
  useAnimate2dMutation,
  useSetActiveVideoClipMutation,
  useSetFeaturedAvatarMutation,
  useVideoClipStatusQuery,
} from '@/services/avatarApi';
import {
  estimateClipCostUsd,
  getProviderMeta,
  mattingAddOnUsd,
} from '@/lib/editorCapabilities';

import { StepError } from '../components/StepError';
import type { WizardState } from '../types';

interface Props {
  state: WizardState;
  update: <K extends keyof WizardState>(k: K, v: WizardState[K]) => void;
  merge: (patch: Partial<WizardState>) => void;
  onComplete: () => void;
  onBackToEngine: () => void;
}

/**
 * Final review screen. Shows a summary, fires animate2d, polls
 * /video-clip-status. On success offers "Set as featured + active" and
 * the parent drops the localStorage draft.
 */
export function Step6Confirm({ state, update, merge, onComplete, onBackToEngine }: Props) {
  const [animate2d] = useAnimate2dMutation();
  const [setActiveVideoClip] = useSetActiveVideoClipMutation();
  const [setFeatured] = useSetFeaturedAvatarMutation();

  const [phase, setPhase] = useState<'idle' | 'submitting' | 'polling' | 'ready' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const provider = useMemo(() => getProviderMeta(state.videoProvider), [state.videoProvider]);
  const baseUsd = estimateClipCostUsd(provider, state.durationSec, state.resolution);
  const totalUsd = (baseUsd + mattingAddOnUsd(state.durationSec, state.videoTransparency)) * state.variants;

  const onGenerate = useCallback(async () => {
    if (!state.avatarId) return;
    setPhase('submitting');
    setErrorMsg(null);
    try {
      const seedNum = state.videoSeed.trim().length > 0 ? Number(state.videoSeed.trim()) : undefined;
      const seed = typeof seedNum === 'number' && Number.isFinite(seedNum) && Number.isInteger(seedNum)
        ? seedNum
        : undefined;
      const res = await animate2d({
        avatarId: state.avatarId,
        prompt: state.motionPrompt,
        provider: state.videoProvider,
        durationSec: state.durationSec,
        resolution: state.resolution,
        variants: state.variants,
        transparency: state.videoTransparency,
        seed,
      }).unwrap();
      update('clipId', res.clipId);
      update('clipStatus', 'queued');
      setPhase('polling');
    } catch (err: unknown) {
      // Surface ALL info we have so a generic "Submission failed" never
      // hides the real cause. RTK Query's FetchBaseQueryError exposes
      // { status, data }; in fetch-failure cases only `error`/`message`
      // are present. Show whichever is most useful + the status code.
      console.error('[Step6Confirm] animate2d failed', err);
      const e = err as {
        status?: number;
        originalStatus?: number;
        data?: { error?: string; message?: string } | string;
        error?: string;
        message?: string;
      };
      const status = e.status ?? e.originalStatus;
      const dataObj = typeof e.data === 'object' ? e.data : undefined;
      const serverMsg =
        dataObj?.error ??
        dataObj?.message ??
        (typeof e.data === 'string' ? e.data : undefined);
      const fallback = e.error ?? e.message ?? 'Submission failed';
      const detail = status != null ? ` (HTTP ${status})` : '';
      setErrorMsg(`${serverMsg ?? fallback}${detail}`);
      setPhase('error');
    }
  }, [animate2d, state, update]);

  // Poll clip status until ready/failed.
  const { data: clip } = useVideoClipStatusQuery(
    { avatarId: state.avatarId ?? '', clipId: state.clipId ?? '' },
    {
      skip: !state.avatarId || !state.clipId || phase !== 'polling',
      pollingInterval: 4000,
    },
  );

  useEffect(() => {
    if (!clip) return;
    if (clip.status === 'ready' && clip.videoUrl) {
      merge({ clipStatus: 'ready', clipUrl: clip.videoUrl });
      setPhase('ready');
    } else if (clip.status === 'failed') {
      setErrorMsg(clip.failureReason ?? 'Clip generation failed');
      merge({ clipStatus: 'failed' });
      setPhase('error');
    }
  }, [clip, merge]);

  const onPublish = useCallback(async () => {
    if (!state.avatarId || !state.clipId) return;
    try {
      await setActiveVideoClip({ avatarId: state.avatarId, clipId: state.clipId }).unwrap();
      if (state.setAsFeatured) {
        await setFeatured(state.avatarId).unwrap();
      }
      onComplete();
    } catch (err) {
      console.error('[Step6Confirm] publish failed', err);
    }
  }, [state, setActiveVideoClip, setFeatured, onComplete]);

  return (
    <section className="flex flex-col gap-5">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-bold text-fg">Review &amp; generate</h1>
        <p className="text-sm text-fg-muted">
          Double-check the choices below. Once you tap generate the clip starts producing — it can take 60–120 seconds.
        </p>
      </header>

      {/* Summary card */}
      <div className="flex flex-col gap-3 rounded-2xl bg-bg-secondary p-4 text-sm">
        <Row label="Title" value={state.title || '—'} />
        <Row label="Prompt" value={truncate(state.prompt, 120)} />
        {state.sourceImageDataUrl && <Row label="Reference photo" value="attached" />}
        <Row label="Model" value={state.model ?? '—'} />
        <Row label="Aspect" value={state.aspectRatio} />
        <Row label="Transparent" value={state.transparent ? 'yes' : 'no'} />
        <Row label="Motion" value={truncate(state.motionPrompt, 120)} />
        <Row label="Engine" value={provider.label} />
        <Row label="Duration · Res" value={`${state.durationSec}s · ${state.resolution}`} />
        <Row label="Variants" value={`${state.variants}×`} />
        <Row label="Background" value={state.videoTransparency} />
      </div>

      {/* Generate or progress */}
      {phase === 'idle' && (
        <button
          type="button"
          onClick={onGenerate}
          className="flex items-center justify-center gap-2 rounded-xl bg-brand py-3 text-sm font-semibold text-brand-fg transition-opacity active:opacity-80"
        >
          Generate clip · ~${totalUsd.toFixed(2)}
        </button>
      )}

      {(phase === 'submitting' || phase === 'polling') && (
        <div className="flex flex-col items-center gap-2 rounded-xl bg-bg-secondary p-4 text-sm text-fg-muted">
          <Loader2 aria-hidden className="h-6 w-6 animate-spin text-brand" />
          <p>
            {phase === 'submitting' ? 'Submitting…' : (clip?.status === 'generating' ? 'Generating clip…' : 'Queued — waiting for engine…')}
          </p>
        </div>
      )}

      {phase === 'error' && (
        <StepError
          title={errorTitleFor(errorMsg)}
          detail={errorDetailFor(errorMsg)}
          actions={[
            { label: 'Retry', onClick: onGenerate, variant: 'primary' },
            { label: 'Tweak engine settings', onClick: onBackToEngine },
          ]}
        />
      )}

      {phase === 'ready' && state.clipUrl && (
        <>
          <div className="overflow-hidden rounded-2xl border border-black/40 bg-black">
            <video
              src={state.clipUrl}
              autoPlay
              loop
              muted
              playsInline
              className="h-full w-full object-contain"
            />
          </div>

          <button
            type="button"
            onClick={() => update('setAsFeatured', !state.setAsFeatured)}
            className={cn(
              'flex items-center justify-between rounded-xl bg-bg-secondary px-3 py-3 text-sm transition-opacity active:opacity-80',
            )}
          >
            <span className="font-semibold text-fg">Set this avatar as my Hub featured</span>
            <span
              aria-hidden
              className={cn(
                'relative h-5 w-9 rounded-full transition-colors',
                state.setAsFeatured ? 'bg-brand' : 'bg-bg-tertiary',
              )}
            >
              <span
                className={cn(
                  'absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform',
                  state.setAsFeatured ? 'translate-x-4' : 'translate-x-0.5',
                )}
              />
            </span>
          </button>

          <button
            type="button"
            onClick={onPublish}
            className="flex items-center justify-center gap-2 rounded-xl bg-brand py-3 text-sm font-semibold text-brand-fg transition-opacity active:opacity-80"
          >
            <Check aria-hidden className="h-4 w-4" />
            Save &amp; finish
          </button>
        </>
      )}
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-[11px] font-semibold uppercase text-fg-muted">{label}</span>
      <span className="text-right text-fg">{value}</span>
    </div>
  );
}

function truncate(s: string, n: number): string {
  if (!s) return '—';
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

/**
 * Map server error tokens to friendly titles + detail text. The server
 * surfaces machine-readable strings via `data.error`; we translate the
 * common ones so users see something actionable instead of a raw token.
 */
function errorTitleFor(raw: string | null): string {
  if (!raw) return "Couldn't produce the clip";
  if (raw === 'user_daily_video2d_cap')         return 'Daily clip limit reached';
  if (raw === 'too_many_concurrent_videos')     return 'Too many clips in flight';
  if (raw === 'avatar_video_capacity_reached')  return "This avatar is full of clips";
  if (raw === 'prompt_too_short')               return 'Motion prompt too short';
  if (raw === 'prompt_too_long')                return 'Motion prompt too long';
  if (raw === 'no_source_image')                return 'Avatar has no source image';
  if (raw === 'avatar_not_active')              return 'Avatar is not active';
  return "Couldn't produce the clip";
}

function errorDetailFor(raw: string | null): string | undefined {
  if (!raw) return undefined;
  if (raw === 'user_daily_video2d_cap')         return 'You hit the per-user 24h clip cap. Wait a bit (the count rolls 24h after each clip) or ask an admin to bump MAX_VIDEO2D_PER_USER_PER_DAY.';
  if (raw === 'too_many_concurrent_videos')     return 'You already have 3 clips generating in parallel. Wait for one to finish, then try again.';
  if (raw === 'avatar_video_capacity_reached')  return 'This avatar already holds 20 video clips. Delete one or pick a different avatar.';
  if (raw === 'prompt_too_short')               return 'Tap Back to the motion step and write a longer description (min 3 characters).';
  if (raw === 'prompt_too_long')                return 'Tap Back and shorten the motion prompt (max 300 characters).';
  return raw;
}
